import type { DailyBar, MarketPoint, PathSource } from "../market-data/types";

export type IntradayPath = {
  source: PathSource;
  seed: string;
  points: MarketPoint[];
};

function hashSeed(seed: string): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededNoise(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function interpolate(key: Array<[number, number]>, x: number): number {
  let index = 0;
  while (index < key.length - 2 && x > key[index + 1][0]) index += 1;
  const [x0, y0] = key[index];
  const [x1, y1] = key[index + 1];
  const t = (x - x0) / (x1 - x0);
  return y0 + (y1 - y0) * t;
}

export function buildSyntheticIntraday(bar: DailyBar, seed: string, count = 180): IntradayPath {
  const range = Math.max(1, bar.high - bar.low);
  const mid = (bar.high + bar.low) / 2;
  const base = hashSeed(seed);
  const key: Array<[number, number]> = [
    [0, bar.open],
    [0.07, Math.min(bar.high, bar.open + range * 0.18)],
    [0.16, bar.low],
    [0.27, Math.min(bar.high, mid + range * 0.1)],
    [0.4, bar.high],
    [0.54, Math.max(bar.low, bar.high - range * 0.22)],
    [0.67, Math.max(bar.low, bar.close - range * 0.26)],
    [0.8, Math.min(bar.high, bar.close + range * 0.18)],
    [0.91, Math.max(bar.low, bar.close - range * 0.12)],
    [1, bar.close]
  ];

  const points: MarketPoint[] = [];
  for (let i = 0; i < count; i += 1) {
    const x = i / (count - 1);
    const ripple = Math.sin(x * 60 + seededNoise(base + 1)) +
      0.55 * Math.sin(x * 137 + seededNoise(base + 2)) +
      0.22 * Math.sin(x * 280 + seededNoise(base + 3));
    const amplitude = Math.min(4.4, range * 0.08);
    const y = Math.max(bar.low + 0.0001, Math.min(bar.high - 0.0001, interpolate(key, x) + ripple * amplitude));
    points.push({ x, y });
  }

  points[0].y = bar.open;
  points[count - 1].y = bar.close;
  points[points.reduce((best, point, i) => (point.y > points[best].y ? i : best), 0)].y = bar.high;
  points[points.reduce((best, point, i) => (point.y < points[best].y ? i : best), 0)].y = bar.low;

  return { source: "synthetic_from_ohlc", seed, points };
}

export function buildDemoIntraday(bar: DailyBar, demoType: "surge" | "crash", count = 180): IntradayPath {
  const key: Array<[number, number]> = demoType === "surge"
    ? [
        [0, bar.open],
        [0.08, bar.open + 1.8],
        [0.18, bar.low + 5.2],
        [0.3, bar.open + 26],
        [0.42, bar.open + 16],
        [0.54, bar.open + 34],
        [0.6, bar.open + 60],
        [0.67, bar.high - 22],
        [0.75, bar.high],
        [0.86, bar.high - 30],
        [1, bar.close]
      ]
    : [
        [0, bar.open],
        [0.08, bar.open + 8],
        [0.18, bar.high],
        [0.3, bar.open - 8],
        [0.42, bar.open + 2],
        [0.53, bar.open - 18],
        [0.6, bar.previousClose - 39],
        [0.66, bar.previousClose - 91],
        [0.73, bar.low],
        [0.86, bar.low + 25],
        [1, bar.close]
      ];

  const points: MarketPoint[] = [];
  for (let i = 0; i < count; i += 1) {
    const x = i / (count - 1);
    const ripple = (Math.sin(x * 60) + 0.55 * Math.sin(x * 137) + 0.22 * Math.sin(x * 280)) * 4.4;
    points.push({ x, y: Math.max(bar.low + 0.0001, Math.min(bar.high - 0.0001, interpolate(key, x) + ripple)) });
  }
  points[0].y = bar.open;
  points[count - 1].y = bar.close;
  points[points.reduce((best, point, i) => (point.y > points[best].y ? i : best), 0)].y = bar.high;
  points[points.reduce((best, point, i) => (point.y < points[best].y ? i : best), 0)].y = bar.low;
  return { source: "demo", seed: demoType, points };
}
